import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { CurecallInput } from '@honestica/curecall-ui'
import { EVariableType } from 'src/types/variable'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { TNewVariableFormValues } from './new-variable-form.component'

export function VariableTypeFields() {
  const { t } = useTranslation()
  const { isFeatureActivated } = useCurrentProject()
  const {
    control,
    watch,
    formState: { errors },
    clearErrors,
  } = useFormContext<TNewVariableFormValues>()

  const selectedType = watch('type')

  const VARIABLE_TYPE_OPTIONS = useMemo(
    () => [
      {
        value: EVariableType.BOOLEAN,
        label: t('pages.configuration.followups.chatbot.variable-modal.variable-type-boolean-label'),
        description: t(
          'pages.configuration.followups.chatbot.variable-modal.variable-type-boolean-description',
        ),
      },
      {
        value: EVariableType.INTEGER_VALUE_RANGE,
        label: t('pages.configuration.followups.chatbot.variable-modal.variable-type-range-label'),
        description: t(
          'pages.configuration.followups.chatbot.variable-modal.variable-type-range-description',
        ),
      },
      ...(isFeatureActivated('patient-documents')
        ? [
            {
              value: EVariableType.MEDIA,
              label: t(
                'pages.configuration.followups.chatbot.variable-modal.variable-type-media-label',
              ),
              description: t(
                'pages.configuration.followups.chatbot.variable-modal.variable-type-media-description',
              ),
            },
          ]
        : []),
      {
        value: EVariableType.STRING,
        label: t('pages.configuration.followups.chatbot.variable-modal.variable-type-string-label'),
        description: t(
          'pages.configuration.followups.chatbot.variable-modal.variable-type-string-description',
        ),
      },
    ],
    [t, isFeatureActivated],
  )

  return (
    <>
      <Controller
        control={control}
        name="type"
        rules={{ required: true }}
        render={({ field }) => (
          <div className="c-flex c-flex-col c-gap-3">
            <label className="c-text-sm c-font-medium c-text-gray-700">
              {t('pages.configuration.followups.chatbot.variable-modal.variable-type')}
              <span className="c-text-red-700 c-ml-1">*</span>
            </label>
            <div className="c-flex c-flex-col c-gap-3">
              {VARIABLE_TYPE_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`c-flex c-items-start c-gap-3 c-p-3 c-border-[1.5px] c-rounded-lg c-cursor-pointer c-transition-all ${
                    field.value === option.value
                      ? 'c-border-primary-400 c-bg-primary-50'
                      : 'c-border-gray-200 c-bg-white hover:c-border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    value={option.value}
                    checked={field.value === option.value}
                    onChange={() => {
                      field.onChange(option.value)
                      if (errors.type) {
                        clearErrors('type')
                      }
                    }}
                    className="c-w-4 c-h-4 c-mt-0.5 c-text-primary focus:c-ring-primary"
                  />
                  <div className="c-flex c-flex-col c-gap-1">
                    <span className="c-text-sm c-font-medium c-text-gray-900">{option.label}</span>
                    <span className="c-text-xs c-text-gray-500">{option.description}</span>
                  </div>
                </label>
              ))}
            </div>
            {errors.type && <span className="c-text-xs c-text-red-600">{t('common.form.required')}</span>}
          </div>
        )}
      />

      {selectedType === EVariableType.INTEGER_VALUE_RANGE && (
        <div className="c-flex c-gap-4">
          <Controller
            control={control}
            name="minValue"
            rules={{
              required: { value: true, message: t('common.form.required') },
              min: { value: 0, message: t('common.form.min-value', { min: 0 }) },
              validate: (value, formValues) => {
                if (
                  value !== undefined &&
                  formValues.maxValue !== undefined &&
                  value >= formValues.maxValue
                ) {
                  return t('pages.configuration.followups.chatbot.variable-modal.min-less-than-max')
                }
                return true
              },
            }}
            render={({ field }) => (
              <CurecallInput
                label={t('pages.configuration.followups.chatbot.variable-modal.variable-min-value')}
                placeholder="0"
                value={field.value?.toString() || ''}
                onChange={(e) => {
                  const numValue = e.target.value ? parseInt(e.target.value, 10) : undefined
                  field.onChange(numValue)
                  if (errors.minValue && numValue !== undefined) {
                    clearErrors('minValue')
                  }
                }}
                isRequired={true}
                errorMessage={errors.minValue?.message}
                wrapperClassName="c-flex-1"
              />
            )}
          />
          <Controller
            control={control}
            name="maxValue"
            rules={{
              required: { value: true, message: t('common.form.required') },
              min: { value: 0, message: t('common.form.min-value', { min: 0 }) },
              validate: (value, formValues) => {
                if (
                  value !== undefined &&
                  formValues.minValue !== undefined &&
                  value <= formValues.minValue
                ) {
                  return t('pages.configuration.followups.chatbot.variable-modal.max-greater-than-min')
                }
                return true
              },
            }}
            render={({ field }) => (
              <CurecallInput
                label={t('pages.configuration.followups.chatbot.variable-modal.variable-max-value')}
                placeholder="100"
                value={field.value?.toString() || ''}
                onChange={(e) => {
                  const numValue = e.target.value ? parseInt(e.target.value, 10) : undefined
                  field.onChange(numValue)
                  if (errors.maxValue && numValue !== undefined) {
                    clearErrors('maxValue')
                  }
                }}
                isRequired={true}
                errorMessage={errors.maxValue?.message}
                wrapperClassName="c-flex-1"
              />
            )}
          />
        </div>
      )}
    </>
  )
}
